import React, { useState, useEffect } from "react";
import ModalLayout from "@/components/layout/ModalLayout";
import { Button } from "@/components/ui/Button";
import { InputBox } from "@/components/ui/InputBox";
import { toast } from "sonner";
import { useUserPasswordChange } from "@/hooks/member/useMember";
import { useQueryClient } from "@tanstack/react-query";
import { LoginResponse } from "@/interface/member";
import { ApiResponse } from "@/interface";

interface ChangeProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (payload: { currentPw: string; newPw: string }) => Promise<void> | void;
}

export default function Change({ isOpen, onClose, onSubmit }: ChangeProps) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // React Query 훅 사용
  const passwordChangeMutation = useUserPasswordChange();
  const queryClient = useQueryClient();

  // 로그인된 사용자 정보 가져오기
  const userInfo = queryClient.getQueryData<LoginResponse>(["user"]);

  // 모달 열릴 때 항상 초기화
  useEffect(() => {
    if (isOpen) {
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
    }
  }, [isOpen]);

  const isMatch = newPw.length > 0 && newPw === confirmPw;
  const canSubmit = currentPw.length > 0 && isMatch && !passwordChangeMutation.isPending;

  const EYE = "/icons/eye.svg";
  const EYE_OFF = "/icons/eyeOff.svg";

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault?.();

    // 클릭 시 검증
    if (!currentPw) {
      toast.warning("현재 비밀번호를 입력해 주세요.");
      return;
    }
    if (!newPw) {
      toast.warning("새 비밀번호를 입력해 주세요.");
      return;
    }
    if (!confirmPw) {
      toast.warning("새 비밀번호 확인을 입력해 주세요.");
      return;
    }
    if (newPw.length < 4) {
      toast.warning("새 비밀번호는 4자 이상 입력해 주세요.");
      return;
    }
    if (newPw !== confirmPw) {
      return;
    }

    // 현재 사용자 name 확인
    if (!userInfo?.name) {
      toast.error("로그인 정보를 찾을 수 없습니다.");
      return;
    }

    // React Query mutation 사용
    passwordChangeMutation.mutate(
      {
        name: userInfo.name,
        currentPassword: currentPw,
        newPassword: newPw,
      },
      {
        onSuccess: (res: ApiResponse) => {
          if (res.code === 200) {
            onClose();
            setCurrentPw("");
            setNewPw("");
            setConfirmPw("");
            setShowCurrent(false);
            setShowNew(false);
            setShowConfirm(false);
            toast.success(res.message);
          } else {
            toast.error(res.message);
          }
        },
        onError: () => {
          // 에러는 useUserPasswordChange에서 이미 처리됨
        },
      }
    );

    // 기존 onSubmit 콜백도 지원 (하위 호환성)
    if (onSubmit) {
      try {
        await onSubmit({ currentPw, newPw });
      } catch (error) {
        console.error("Custom onSubmit error:", error);
      }
    }
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      title="비밀번호 변경"
      maxWidth="max-w-md"
      zIndexClass="z-[9999]"
      usePortal
    >
      <form onSubmit={handleSubmit}>
        {/* 현재 비밀번호 */}
        <div className="mb-4">
          <InputBox
            type={showCurrent ? "text" : "password"}
            label="현재 비밀번호"
            placeholder="입력"
            value={currentPw}
            onChange={e => setCurrentPw(e.target.value)}
            showIcon
            iconSrc={showCurrent ? EYE_OFF : EYE}
            onIconClick={() => setShowCurrent(s => !s)}
            iconMinimal
          />
        </div>

        {/* 새 비밀번호 */}
        <div className="mb-4">
          <InputBox
            type={showNew ? "text" : "password"}
            label="새 비밀번호"
            placeholder="입력"
            value={newPw}
            onChange={e => setNewPw(e.target.value)}
            showIcon
            iconSrc={showNew ? EYE_OFF : EYE}
            onIconClick={() => setShowNew(s => !s)}
            iconMinimal
          />
        </div>

        {/* 새 비밀번호 확인 */}
        <div className="mb-6">
          <InputBox
            type={showConfirm ? "text" : "password"}
            label="새 비밀번호 확인"
            placeholder="입력"
            value={confirmPw}
            onChange={e => setConfirmPw(e.target.value)}
            showIcon
            iconSrc={showConfirm ? EYE_OFF : EYE}
            onIconClick={() => setShowConfirm(s => !s)}
            iconMinimal
          />
          {confirmPw.length > 0 && (
            <p className={`mt-2 text-xs ${isMatch ? "text-green-600" : "text-red-600"}`}>
              {isMatch ? "비밀번호가 일치합니다." : "비밀번호가 일치하지 않습니다."}
            </p>
          )}
        </div>

        {/* 안내 박스 */}
        <div className="mb-6 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
          <div className="font-semibold text-gray-700 mb-2">비밀번호 설정 안내</div>
          <ul className="list-disc pl-5 space-y-1">
            <li>영문, 숫자, 특수문자를 조합하여 사용하는 것을 권장합니다.</li>
            <li>정기적으로 비밀번호를 변경해주세요.</li>
          </ul>
        </div>

        {/* 하단 버튼 */}
        <div className="flex items-center gap-3 mt-6">
          <div className="flex justify-start w-full">
            <Button
              type="button"
              variant="white"
              className="py-[10px]"
              onClick={onClose}
              disabled={passwordChangeMutation.isPending}
            >
              취소
            </Button>
          </div>
          <div className="flex justify-end w-full">
            <Button type="submit" variant="primary" className="py-[10px]" disabled={passwordChangeMutation.isPending}>
              {passwordChangeMutation.isPending ? "변경 중..." : "변경"}
            </Button>
          </div>
        </div>
      </form>
    </ModalLayout>
  );
}
